import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from './cx'
import s from './SegmentedControl.module.css'

/**
 * The one segmented toggle — a small pill group with a selected segment. Replaces
 * the persona/view/week-day-month toggles each page rolled by hand.
 *
 * Semantics: a RADIOGROUP (UX-12 / #392). It used to say `role="tablist"` while
 * implementing none of the tabs contract — no roving focus, no arrow keys, no
 * `aria-controls`, no panels — so a screen reader promised behaviour the widget
 * did not have. What it actually is is a mutually-exclusive form value, which is
 * the radio pattern: one tab stop (the checked segment), arrows move AND select,
 * `aria-checked` names the state.
 *
 * `Home` and `End` are part of that contract and not a flourish: two browser
 * specs press them, and a hand-rolled copy of this pattern that omitted them
 * reached CI in #2349. That is the argument for `presentation: 'icon'` living
 * here rather than in a second control — see the prop.
 */

/** One option. Everything past `value`/`label` is additive and optional. */
export interface SegmentedOption<T extends string> {
  value: T
  /** Always required: it is the visible text, or the accessible name of a glyph. */
  label: string
  /** Drawn INSTEAD of the label under `presentation="icon"`. */
  icon?: ReactNode
  /** A host-supplied state mark, rendered only on the SELECTED option. */
  mark?: ReactNode
  /** This option's own test handle, where a surface addresses one choice. */
  testId?: string
  /** Extra data attributes for this option. */
  data?: Record<string, string>
  /**
   * A count that RIDES the option (#2502 §5.3: the Timeline's unread count on
   * "Your account"). Drawn as a seated micro-pill after the words, and ONLY
   * when greater than zero — the design's rule is "no zero counts on chips or
   * tabs", so 0, null and absent all draw nothing. It is part of the option's
   * accessible name ("Your account, 3 unread" when `countLabel` says so), and
   * it never replaces the label.
   */
  count?: number | null
  /** The count's words for assistive technology ("unread"). Defaults to none. */
  countLabel?: string
}

/**
 * `true` while the channel's options occupy more than one line (#2406 batch-3
 * visual review, ROOT ruling 81344 R2).
 *
 * The channel may wrap inside a narrow container (b3-controls), and a WRAPPED
 * channel takes a rounded-slot radius so its corner options are not shaved by a
 * lozenge. A ONE-ROW channel must keep fully round ends — and CSS cannot tell
 * the two apart: the radius batch 3 chose for the wrapped case was 20px on a
 * mouse, which squared the ends of every one-row channel taller than 40px (the
 * InsightCard switch, 52px). So the kit measures: a channel is wrapped when an
 * option starts on a lower line than the first. Measured before paint and on
 * every resize of the channel, so neither form ever flashes the other.
 */
function useWrapped(ref: React.RefObject<HTMLDivElement | null>, words: string) {
  const [wrapped, setWrapped] = useState(false)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => {
      const segs = el.querySelectorAll<HTMLElement>(':scope > [role="radio"]')
      const first = segs[0]
      const last = segs[segs.length - 1]
      setWrapped(first != null && last != null && last.offsetTop > first.offsetTop + 1)
    }
    check()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, words])
  return wrapped
}

export function SegmentedControl<T extends string>({
  options, value, onChange, size = 'md', label, testId, presentation = 'label',
}: {
  options: SegmentedOption<T>[]
  value: T | null
  onChange: (v: T) => void
  size?: 'sm' | 'md'
  /** the group's accessible name — "Persona", "Channel", "Capture kind", … */
  label?: string
  /**
   * Addressable when a surface carries SEVERAL of these (#2276): a record page
   * has one per relationship section, and "the segmented control" stops naming
   * anything the moment there is more than one.
   */
  testId?: string
  /**
   * `icon` draws the glyph and drops the visible text (#2349 §D3).
   *
   * The LABEL does not go anywhere: it becomes the option's accessible name and
   * its tooltip, so what a compact form drops is pixels and never meaning. A
   * host that passes no `icon` on an option gets the label drawn, whatever this
   * says, because a blank segment is not a compact one.
   *
   * ── why this lives HERE and not in a second control ────────────────────────
   * It was a second control for one afternoon. #2349's per-type view selector
   * was hand-rolled as its own radiogroup — five buttons, roving tabindex,
   * arrow keys — and it shipped WITHOUT `Home`/`End`, which this component has
   * always had. Two browser specs caught it (`map-lens`, `record-relations`),
   * and the lesson is the one `ui-and-experience.md` §1 opens with: a copy is
   * correct on the day it is written and diverges on every day after. So the
   * icon presentation is a prop on the one radiogroup rather than a second
   * implementation of the radio pattern.
   *
   * `both` draws the glyph AND the words (#2406, leading design: the record's
   * Connections switcher reads [▭ Cards] [▦ Grid] [⚭ Graph]). The words stay
   * the accessible name, so it needs no `aria-label` of its own.
   */
  presentation?: 'label' | 'icon' | 'both'
}) {
  const ref = useRef<HTMLDivElement>(null)
  const wrapped = useWrapped(ref, options.map((o) => o.label).join('\u0000'))
  const selected = options.findIndex((o) => o.value === value)
  const pick = (i: number) => {
    const o = options[(i + options.length) % options.length]
    if (!o) return
    onChange(o.value)
    // Roving tabindex: focus follows the selection so the group stays one stop.
    ref.current?.querySelectorAll<HTMLButtonElement>('button')[(i + options.length) % options.length]?.focus()
  }
  const onKey = (e: React.KeyboardEvent, i: number) => {
    switch (e.key) {
      case 'ArrowRight': case 'ArrowDown': e.preventDefault(); pick(i + 1); break
      case 'ArrowLeft': case 'ArrowUp': e.preventDefault(); pick(i - 1); break
      case 'Home': e.preventDefault(); pick(0); break
      case 'End': e.preventDefault(); pick(options.length - 1); break
    }
  }
  return (
    <div ref={ref} className={cx(s.group, s[size])} role="radiogroup" aria-label={label}
      data-testid={testId} data-wrapped={wrapped || undefined}>
      {options.map((o, i) => (
        <button
          key={o.value}
          /* The option's own handle and data, for a surface that addresses a
             single choice rather than the group. Spread FIRST so nothing here
             can overwrite the semantics below it. */
          {...(o.data ?? {})}
          data-testid={o.testId}
          title={presentation === 'icon' && o.icon ? o.label : undefined}
          aria-label={presentation === 'icon' && o.icon ? o.label : undefined}
          // A <button> inside a <form> defaults to type="submit". This one is
          // a toggle, not a submit — without this, choosing a segment SUBMITS
          // the surrounding form. Found by putting it in the capture panel's
          // form (the former embedded extension), where picking a kind sent the capture.
          type="button"
          role="radio"
          aria-checked={i === selected}
          // One tab stop for the whole group — every segment being tabbable
          // is its own accessibility problem on a page with five of these.
          tabIndex={i === (selected < 0 ? 0 : selected) ? 0 : -1}
          className={cx(s.seg, i === selected && s.on,
            presentation === 'icon' && o.icon ? s.iconSeg : undefined,
            presentation === 'both' && o.icon ? s.bothSeg : undefined)}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {presentation === 'icon' && o.icon ? o.icon
            : presentation === 'both' && o.icon ? <>{o.icon}{o.label}</>
            : o.label}
          {o.count != null && o.count > 0 && (
            <span className={cx(s.count, 'tnum')} data-segment-count="">
              <span className={s.srOnly}>, </span>{o.count}
              {o.countLabel ? <span className={s.srOnly}> {o.countLabel}</span> : null}
            </span>
          )}
          {/* A mark the HOST supplies — a state about this option that the
              radio semantics cannot carry, such as "your request for this was
              refused". Rendered after the content so it can be positioned
              against the segment; never the only channel, because the host is
              required to say the same thing in words elsewhere. */}
          {i === selected && o.mark}
        </button>
      ))}
    </div>
  )
}
